import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';
import { BaseService } from './base.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

type SupportedCurrencyCode = 'USD' | 'PKR';
type SupportedCountryCode = 'US' | 'PK';

interface RuntimeCurrencyConfig {
  autoByIp: boolean;
  defaultCurrencyCode: SupportedCurrencyCode;
  defaultCountryCode: SupportedCountryCode;
  ipCountryLookupUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class CurrencySettingsService {
  private readonly storageCurrencyKey = 'app.currency.code';
  private readonly storageCountryKey = 'app.currency.country';
  private readonly storageAutoByIpKey = 'app.currency.autoByIp';

  private config: RuntimeCurrencyConfig = {
    autoByIp: true,
    defaultCurrencyCode: 'USD',
    defaultCountryCode: 'US',
    ipCountryLookupUrl: 'https://ipwho.is/'
  };

  private _currencyCode: SupportedCurrencyCode = 'USD';
  private _countryCode: SupportedCountryCode = 'US';

  constructor(private _bs: BaseService) {}

  get currencyCode(): SupportedCurrencyCode {
    return this._currencyCode;
  }

  get countryCode(): SupportedCountryCode {
    return this._countryCode;
  }

  get currencyLabel(): string {
    return this._currencyCode === 'PKR' ? 'PKR' : 'USD';
  }

  async initialize(): Promise<void> {
    this.loadRuntimeConfig();
    this.syncCurrencyFromBusinessSetup();
  }

  setCurrencyPreference(currencyCode: SupportedCurrencyCode, autoByIp = false): void {
    const normalizedCurrency = this.normalizeCurrencyCode(currencyCode) ?? this.config.defaultCurrencyCode;
    const country = this.countryFromCurrency(normalizedCurrency);
    this.applyCurrency(normalizedCurrency, country);
    this.persistState(autoByIp);
  }

  setAutoByIp(autoByIp: boolean): void {
    this.persistState(autoByIp);
  }

  syncCurrencyFromBusinessSetup(userOrSetup?: any): void {
    const username =
      userOrSetup?.username ??
      userOrSetup?.email ??
      GlobalService.userInfo?.username ??
      null;

    const setup =
      userOrSetup?.businessSetup?.[0] ??
      userOrSetup?.businessSetup ??
      userOrSetup ??
      GlobalService.userInfo?.businessSetup?.[0] ??
      null;

    const companyName =
      setup?.companyName ??
      GlobalService.userInfo?.businessSetup?.[0]?.companyName ??
      null;

    const isAdminAccount =
      username === 'user.admin@lincsell.com' ||
      String(companyName ?? '').trim().toUpperCase() === 'ADMINISTRATION';

    if (isAdminAccount) {
      this.applyCurrency('USD', 'US');
      this.persistState(false);
      return;
    }

    const currencyList = Array.isArray(setup?.currencyList) ? setup.currencyList : [];
    const firstCurrency = currencyList.length > 0 ? currencyList[0] : null;
    const requestedCode =
      firstCurrency?.Code ??
      firstCurrency?.code ??
      firstCurrency?.currencyCode ??
      firstCurrency?.CurrencyCode ??
      null;

    const normalizedCurrency =
      this.normalizeCurrencyCode(requestedCode) ??
      this.config.defaultCurrencyCode ??
      'USD';
    const country = this.countryFromCurrency(normalizedCurrency);
    this.applyCurrency(normalizedCurrency, country);
    this.persistState(false);
  }

  getCurrencyList(): Observable<Array<{ code: string; label: string; name?: string; symbol?: string; raw?: any }>> {
    const url = `${environment.BusinessBaseURL}GetCurrency`;
    return this._bs.httpGet(url, false).pipe(
      map((res: any) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        return list
          .map((item: any) => {
            const code = String(
              item?.currencyCode ??
              item?.code ??
              item?.Code ??
              item?.currency ??
              item?.CurrencyCode ??
              ''
            ).trim().toUpperCase();
            const name = String(
              item?.currencyName ??
              item?.name ??
              item?.title ??
              item?.Title ??
              item?.CurrencyName ??
              ''
            ).trim();
            const symbol = String(
              item?.currencySymbol ??
              item?.symbol ??
              item?.Symbol ??
              item?.CurrencySymbol ??
              ''
            ).trim();

            if (!code) return null;
            const label = name
              ? `${name}${symbol ? ` (${symbol})` : ''}`
              : code;
            return { code, label, name, symbol, raw: item };
          })
          .filter((item: any): item is { code: string; label: string; name?: string; symbol?: string; raw?: any } => !!item);
      }),
      catchError(() => of([]))
    );
  }

  private loadRuntimeConfig(): void {
    const runtimeConfig = (environment as any)?.currencyConfig || {};

    const defaultCurrencyCode = this.normalizeCurrencyCode(runtimeConfig.defaultCurrencyCode) ?? 'USD';
    const defaultCountryCode = this.normalizeCountryCode(runtimeConfig.defaultCountryCode)
      ?? this.countryFromCurrency(defaultCurrencyCode);

    this.config = {
      autoByIp: runtimeConfig.autoByIp !== false,
      defaultCurrencyCode,
      defaultCountryCode,
      ipCountryLookupUrl: String(runtimeConfig.ipCountryLookupUrl || 'https://ipwho.is/').trim()
    };
  }

  private persistState(autoByIp: boolean): void {
    localStorage.setItem(this.storageAutoByIpKey, String(autoByIp));
    localStorage.setItem(this.storageCurrencyKey, this._currencyCode);
    localStorage.setItem(this.storageCountryKey, this._countryCode);
  }

  private readPersistedAutoByIp(): boolean | null {
    const raw = localStorage.getItem(this.storageAutoByIpKey);
    if (raw === null) return null;
    return String(raw).toLowerCase() !== 'false';
  }

  private async detectCountryCode(): Promise<SupportedCountryCode | null> {
    const fromIpApi = await this.fetchCountryCodeFromIp(this.config.ipCountryLookupUrl);
    if (fromIpApi) return fromIpApi;

    const fromFallbackApi = await this.fetchCountryCodeFromIp('https://api.country.is/');
    if (fromFallbackApi) return fromFallbackApi;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timezone.includes('Karachi')) return 'PK';
    if (timezone.includes('America/')) return 'US';

    return null;
  }

  private async fetchCountryCodeFromIp(url: string): Promise<SupportedCountryCode | null> {
    if (!url) return null;
    try {
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) return null;
      const payload: any = await response.json();
      const code = this.normalizeCountryCode(
        payload?.country_code || payload?.country || payload?.countryCode
      );
      return code;
    } catch {
      return null;
    }
  }

  private applyCurrency(currencyCode: SupportedCurrencyCode, countryCode: SupportedCountryCode): void {
    this._currencyCode = currencyCode;
    this._countryCode = countryCode;
    GlobalService.setCurrencyContext(currencyCode, countryCode);
  }

  private currencyFromCountry(countryCode: SupportedCountryCode): SupportedCurrencyCode {
    return countryCode === 'PK' ? 'PKR' : 'USD';
  }

  private countryFromCurrency(currencyCode: SupportedCurrencyCode): SupportedCountryCode {
    return currencyCode === 'PKR' ? 'PK' : 'US';
  }

  private normalizeCurrencyCode(value: any): SupportedCurrencyCode | null {
    const code = String(value || '').trim().toUpperCase();
    if (code === 'PKR') return 'PKR';
    if (code === 'USD') return 'USD';
    return null;
  }

  private normalizeCountryCode(value: any): SupportedCountryCode | null {
    const code = String(value || '').trim().toUpperCase();
    if (code === 'PK' || code === 'PAKISTAN') return 'PK';
    if (code === 'US' || code === 'USA' || code === 'UNITEDSTATES') return 'US';
    return null;
  }
}
